import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { getToken, getUser, setToken, setUser, removeToken, removeUser, parseJWT, getUserRole } from '../utils/auth';

// Initial state
const initialState = {
  user: null,
  token: null,
  role: null,
  isAuthenticated: false,
  loading: true
};

// Action types
const USER_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_TOKEN: 'SET_TOKEN'
};

// Reducer
const userReducer = (state, action) => {
  switch (action.type) {
    case USER_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case USER_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        role: action.payload.role,
        isAuthenticated: true,
        loading: false
      };
    
    case USER_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        role: null,
        isAuthenticated: false,
        loading: false
      };
    
    case USER_ACTIONS.SET_USER:
      return { ...state, user: action.payload };
    
    case USER_ACTIONS.SET_TOKEN:
      const token = action.payload;
      const decoded = parseJWT(token);
      const role = getUserRole(token);
      return {
        ...state,
        token,
        role,
        isAuthenticated: !!decoded
      };
    
    default:
      return state;
  }
};

// Create context
const UserContext = createContext();

// Provider component
export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  // Initialize user state from localStorage
  useEffect(() => {
    const initializeUser = () => {
      const token = getToken();
      const user = getUser();
      
      if (token && user) {
        const decoded = parseJWT(token);
        const role = getUserRole(token);
        
        if (decoded) {
          dispatch({
            type: USER_ACTIONS.LOGIN_SUCCESS,
            payload: { user, token, role }
          });
        } else {
          // Token is invalid, clear storage
          removeToken();
          removeUser();
          dispatch({ type: USER_ACTIONS.SET_LOADING, payload: false });
        }
      } else {
        dispatch({ type: USER_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeUser();
  }, []);

  // Login function
  const login = async (loginData) => {
    try {
      dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
      
      // Store token and user data
      setToken(loginData.token);
      setUser(loginData.user);
      
      const role = getUserRole(loginData.token);
      
      dispatch({
        type: USER_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: loginData.user,
          token: loginData.token,
          role
        }
      });
      
      return { success: true };
    } catch (error) {
      dispatch({ type: USER_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    removeToken();
    removeUser();
    dispatch({ type: USER_ACTIONS.LOGOUT });
  };

  const value = {
    ...state,
    login,
    logout
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
